import { useEffect, useState } from 'react';
import { clienteService, pagoService, prestamoService } from '../../services/api';
import '../../styles/Reportes.css';

const EstadoCuenta = () => {
  const [clientes, setClientes] = useState([]);
  const [clienteSeleccionado, setClienteSeleccionado] = useState('');
  const [prestamoSeleccionado, setPrestamoSeleccionado] = useState('');
  const [prestamos, setPrestamos] = useState([]);
  const [estadoCuenta, setEstadoCuenta] = useState(null);
  const [cargando, setCargando] = useState(false);

  useEffect(() => {
    cargarClientes();
  }, []);

  const cargarClientes = async () => {
    try {
      const response = await clienteService.getAll();
      setClientes(response.data.clientes || []);
    } catch (error) {
      console.error('Error al cargar clientes:', error);
    }
  };

  const handleClienteChange = async (clienteId) => {
    setClienteSeleccionado(clienteId);
    setPrestamoSeleccionado('');
    setEstadoCuenta(null);

    if (clienteId) {
      try {
        const response = await prestamoService.getAll({ cliente_id: clienteId });
        const prestamosActivos = (response.data.prestamos || []).filter(
          p => p.estado === 'activo'
        );
        setPrestamos(prestamosActivos);
      } catch (error) {
        console.error('Error al cargar préstamos:', error);
      }
    } else {
      setPrestamos([]);
    }
  };

  const generarEstadoCuenta = async () => {
    if (!prestamoSeleccionado) {
      alert('Por favor selecciona un préstamo');
      return;
    }

    setCargando(true);
    try {
      const [prestamoRes, pagosRes, clienteRes] = await Promise.all([
        prestamoService.getById(prestamoSeleccionado),
        pagoService.getByPrestamo(prestamoSeleccionado),
        clienteService.getById(clienteSeleccionado)
      ]);

      const prestamo = prestamoRes.data;
      const pagos = pagosRes.data.pagos || [];
      const cliente = clienteRes.data;

      // Calcular estado de cuenta
      const totalPagado = pagos.reduce((sum, p) => sum + p.monto_total, 0);
      const capitalPagado = pagos.reduce((sum, p) => sum + p.monto_capital, 0);
      const interesPagado = pagos.reduce((sum, p) => sum + p.monto_interes, 0);
      const moraPagada = pagos.reduce((sum, p) => sum + p.monto_mora, 0);

      const saldoCapital = prestamo.capital - capitalPagado;
      const saldoTotal = prestamo.total - totalPagado;
      const cuotasPendientes = prestamo.cuotas - prestamo.cuotas_pagadas;
      const montoCuota = prestamo.total / prestamo.cuotas;

      setEstadoCuenta({
        cliente,
        prestamo,
        pagos,
        resumen: {
          totalPagado,
          capitalPagado,
          interesPagado,
          moraPagada,
          saldoCapital,
          saldoTotal,
          cuotasPendientes,
          montoCuota
        }
      });
    } catch (error) {
      alert('Error al generar estado de cuenta');
      console.error(error);
    } finally {
      setCargando(false);
    }
  };

  const formatearFecha = (fecha) => {
    return new Date(fecha).toLocaleDateString('es-DO');
  };

  return (
    <div className="reporte-detalle">
      <div className="reporte-header">
        <h1>📊 Estado de Cuenta</h1>
        <p>Estado de cuenta detallado de clientes</p>
      </div>

      <div className="filtros-container">
        <div className="filtro-grupo">
          <label>Cliente:</label>
          <select
            value={clienteSeleccionado}
            onChange={(e) => handleClienteChange(e.target.value)}
            className="input-select"
          >
            <option value="">--- Seleccione un Cliente ---</option>
            {clientes.map(cliente => (
              <option key={cliente.id} value={cliente.id}>
                {cliente.cedula} - {cliente.nombres} {cliente.apellidos}
              </option>
            ))}
          </select>
        </div>

        <div className="filtro-grupo">
          <label>Préstamo:</label>
          <select
            value={prestamoSeleccionado}
            onChange={(e) => setPrestamoSeleccionado(e.target.value)}
            className="input-select"
            disabled={!clienteSeleccionado}
          >
            <option value="">--- Seleccione un Préstamo ---</option>
            {prestamos.map(prestamo => (
              <option key={prestamo.id} value={prestamo.id}>
                ID: {prestamo.id} - ${prestamo.total.toFixed(2)} ({prestamo.modalidad})
              </option>
            ))}
          </select>
        </div>

        <button
          onClick={generarEstadoCuenta}
          className="btn-buscar"
          disabled={!prestamoSeleccionado}
        >
          Generar Estado de Cuenta
        </button>
      </div>

      {cargando && (
        <div className="loading">Generando estado de cuenta...</div>
      )}

      {estadoCuenta && (
        <>
          {/* Información del Cliente y Préstamo */}
          <div className="info-prestamo">
            <h3>Información General</h3>
            <div className="info-grid">
              <div><strong>Cliente:</strong> {estadoCuenta.cliente.nombres} {estadoCuenta.cliente.apellidos}</div>
              <div><strong>Cédula:</strong> {estadoCuenta.cliente.cedula}</div>
              <div><strong>Préstamo ID:</strong> {estadoCuenta.prestamo.id}</div>
              <div><strong>Modalidad:</strong> {estadoCuenta.prestamo.modalidad}</div>
              <div><strong>Fecha Inicio:</strong> {formatearFecha(estadoCuenta.prestamo.fecha_inicio)}</div>
              <div><strong>Estado:</strong> <span className={`badge ${estadoCuenta.prestamo.estado}`}>{estadoCuenta.prestamo.estado}</span></div>
            </div>
          </div>

          {/* Resumen Financiero */}
          <div className="resumen-cards">
            <div className="card-resumen">
              <div className="card-icono">💰</div>
              <div className="card-info">
                <div className="card-valor">${estadoCuenta.resumen.totalPagado.toFixed(2)}</div>
                <div className="card-label">Total Pagado</div>
              </div>
            </div>
            <div className="card-resumen">
              <div className="card-icono">�</div>
              <div className="card-info">
                <div className="card-valor">${estadoCuenta.resumen.saldoTotal.toFixed(2)}</div>
                <div className="card-label">Saldo Pendiente</div>
              </div>
            </div>
            <div className="card-resumen">
              <div className="card-icono">📈</div>
              <div className="card-info">
                <div className="card-valor">{estadoCuenta.resumen.cuotasPendientes}</div>
                <div className="card-label">Cuotas Pendientes</div>
              </div>
            </div>
            <div className="card-resumen">
              <div className="card-icono">💵</div>
              <div className="card-info">
                <div className="card-valor">${estadoCuenta.resumen.montoCuota.toFixed(2)}</div>
                <div className="card-label">Monto Cuota</div>
              </div>
            </div>
          </div>

          {/* Detalle del Préstamo */}
          <div className="tabla-container">
            <h3>Detalle del Préstamo</h3>
            <table className="tabla-reporte">
              <thead>
                <tr>
                  <th>Concepto</th>
                  <th>Monto Original</th>
                  <th>Pagado</th>
                  <th>Saldo</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><strong>Capital</strong></td>
                  <td>${estadoCuenta.prestamo.capital.toFixed(2)}</td>
                  <td>${estadoCuenta.resumen.capitalPagado.toFixed(2)}</td>
                  <td className="destacado">${estadoCuenta.resumen.saldoCapital.toFixed(2)}</td>
                </tr>
                <tr>
                  <td><strong>Interés</strong></td>
                  <td>${estadoCuenta.prestamo.interes.toFixed(2)}</td>
                  <td>${estadoCuenta.resumen.interesPagado.toFixed(2)}</td>
                  <td>${(estadoCuenta.prestamo.interes - estadoCuenta.resumen.interesPagado).toFixed(2)}</td>
                </tr>
                <tr>
                  <td><strong>Mora</strong></td>
                  <td>-</td>
                  <td>${estadoCuenta.resumen.moraPagada.toFixed(2)}</td>
                  <td>-</td>
                </tr>
              </tbody>
              <tfoot>
                <tr className="totales">
                  <td><strong>TOTAL</strong></td>
                  <td><strong>${estadoCuenta.prestamo.total.toFixed(2)}</strong></td>
                  <td><strong>${estadoCuenta.resumen.totalPagado.toFixed(2)}</strong></td>
                  <td className="destacado"><strong>${estadoCuenta.resumen.saldoTotal.toFixed(2)}</strong></td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Historial de Pagos */}
          {estadoCuenta.pagos.length > 0 && (
            <div className="tabla-container">
              <h3>Historial de Pagos</h3>
              <table className="tabla-reporte">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Fecha</th>
                    <th>Capital</th>
                    <th>Interés</th>
                    <th>Mora</th>
                    <th>Total</th>
                    <th>Tipo</th>
                  </tr>
                </thead>
                <tbody>
                  {estadoCuenta.pagos.map((pago, index) => (
                    <tr key={pago.id}>
                      <td>{index + 1}</td>
                      <td>{formatearFecha(pago.fecha_pago)}</td>
                      <td>${pago.monto_capital.toFixed(2)}</td>
                      <td>${pago.monto_interes.toFixed(2)}</td>
                      <td>${pago.monto_mora.toFixed(2)}</td>
                      <td className="destacado">${pago.monto_total.toFixed(2)}</td>
                      <td><span className={`badge ${pago.tipo_pago}`}>{pago.tipo_pago}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {!estadoCuenta && !cargando && (
        <div className="sin-datos">
          <p>Selecciona un cliente y un préstamo para generar el estado de cuenta</p>
        </div>
      )}
    </div>
  );
};

export default EstadoCuenta;
